/**
 * S2S postback tracking — dash data-layer client (feature #159).
 *
 * Typed wrappers around `apiClient` for the `/api/v1/s2s-postback` surface:
 *
 *   POST   /urls                           — register a postback URL template
 *   GET    /urls?advertiser_id=&optional   — list tenant templates
 *   DELETE /urls/:id                       — remove a template
 *   POST   /clicks                         — seed a click (mainly for ops testing)
 *   POST   /simulate                       — dry-run macro render
 *   GET    /fire?click_id=&event=&...      — fire postback (partner-facing)
 *   POST   /fire                           — same as GET, JSON body
 *   GET    /events?limit=                  — recent fires log
 *
 * The page uses the *admin* endpoints (urls, simulate, events). The fire
 * endpoint is for partner integrations — the page renders the tenant's
 * postback URL so operators can copy it.
 *
 * Pure helpers are exported for unit tests (the page uses them directly and
 * the tests verify the contract without rendering React).
 */

import { apiClient, ApiError } from "@/api-client";

// ---------------------------------------------------------------------------
// Types — mirror API shapes.
// ---------------------------------------------------------------------------

export type PostbackMethod = "GET" | "POST";
export type PostbackStatus = "fired" | "duplicate" | "unknown_click" | "rejected";

export interface PostbackUrlView {
	id: string;
	advertiser_id: string;
	label: string;
	url_template: string;
	method: PostbackMethod;
	events_csv: string;
	enabled: boolean;
	created_at: number;
}

export interface PostbackEventView {
	id: string;
	click_id: string;
	event: string;
	payout: number;
	currency: string;
	transaction_id: string | null;
	status: PostbackStatus;
	rendered_urls: string[];
	created_at: number;
}

export interface SimulateRenderItem {
	template_id: string;
	label: string;
	method: PostbackMethod;
	rendered_url: string;
}

export interface SimulateResponse {
	click_id: string;
	event: string;
	rendered: SimulateRenderItem[];
}

// ---------------------------------------------------------------------------
// Supported macros — source of truth for the helper panel in the UI.
// ---------------------------------------------------------------------------

export const SUPPORTED_MACROS = [
	"click_id",
	"event",
	"payout",
	"currency",
	"transaction_id",
	"advertiser_id",
	"offer_id",
	"affiliate_id",
	"sub1",
	"sub2",
	"sub3",
	"sub4",
	"sub5",
	"timestamp",
] as const;

export type MacroKey = (typeof SUPPORTED_MACROS)[number];

export const MACRO_DESCRIPTIONS: Record<MacroKey, string> = {
	click_id: "Unique click correlation id",
	event: "Event name (conversion, install, …)",
	payout: "Numeric payout amount",
	currency: "3-char currency code",
	transaction_id: "Partner-side transaction id (dedup input)",
	advertiser_id: "Advertiser that owns the offer",
	offer_id: "Offer the click was for",
	affiliate_id: "Publisher who generated the click",
	sub1: "Partner sub-id 1 passthrough",
	sub2: "Partner sub-id 2 passthrough",
	sub3: "Partner sub-id 3 passthrough",
	sub4: "Partner sub-id 4 passthrough",
	sub5: "Partner sub-id 5 passthrough",
	timestamp: "UNIX seconds at fire time",
};

// ---------------------------------------------------------------------------
// URL template validation — mirrors server-side.
// ---------------------------------------------------------------------------

const MACRO_TOKEN_RE = /\{([a-zA-Z_][a-zA-Z0-9_]*)\}/g;

export function extractMacros(template: string): string[] {
	if (typeof template !== "string") return [];
	const out: string[] = [];
	for (const m of template.matchAll(MACRO_TOKEN_RE)) {
		const key = m[1];
		if (typeof key === "string" && !out.includes(key)) out.push(key);
	}
	return out;
}

export function isHttpUrlTemplate(template: string): boolean {
	if (typeof template !== "string") return false;
	const trimmed = template.trim();
	if (!trimmed || trimmed.length > 2048) return false;
	const probe = trimmed.replace(MACRO_TOKEN_RE, "x");
	try {
		const u = new URL(probe);
		return u.protocol === "http:" || u.protocol === "https:";
	} catch {
		return false;
	}
}

export function isValidEventsCsv(raw: string): boolean {
	if (typeof raw !== "string") return false;
	const t = raw.trim();
	if (t === "" || t === "*") return true;
	const parts = t.split(",").map((s) => s.trim()).filter((s) => s.length > 0);
	if (parts.length === 0 || parts.length > 32) return false;
	for (const p of parts) {
		if (p.length > 64) return false;
		if (!/^[a-zA-Z0-9_.-]+$/.test(p)) return false;
	}
	return true;
}

// ---------------------------------------------------------------------------
// Client-side macro substitution — same rules as server. Used by the simulate
// preview to keep the UI instant; final truth is still the server render.
// ---------------------------------------------------------------------------

export interface ClientMacroCtx {
	click_id: string;
	event: string;
	payout: number | string;
	currency: string;
	transaction_id: string | null;
	advertiser_id: string;
	offer_id: string;
	affiliate_id: string;
	sub1: string | null;
	sub2: string | null;
	sub3: string | null;
	sub4: string | null;
	sub5: string | null;
	timestamp: number;
}

export function renderPreview(template: string, ctx: ClientMacroCtx): string {
	return (template ?? "").replace(MACRO_TOKEN_RE, (_m, key: string) => {
		switch (key) {
			case "click_id":
				return encodeURIComponent(ctx.click_id ?? "");
			case "event":
				return encodeURIComponent(ctx.event ?? "");
			case "payout":
				return encodeURIComponent(String(ctx.payout ?? ""));
			case "currency":
				return encodeURIComponent(ctx.currency ?? "");
			case "transaction_id":
				return encodeURIComponent(ctx.transaction_id ?? "");
			case "advertiser_id":
				return encodeURIComponent(ctx.advertiser_id ?? "");
			case "offer_id":
				return encodeURIComponent(ctx.offer_id ?? "");
			case "affiliate_id":
				return encodeURIComponent(ctx.affiliate_id ?? "");
			case "sub1":
				return encodeURIComponent(ctx.sub1 ?? "");
			case "sub2":
				return encodeURIComponent(ctx.sub2 ?? "");
			case "sub3":
				return encodeURIComponent(ctx.sub3 ?? "");
			case "sub4":
				return encodeURIComponent(ctx.sub4 ?? "");
			case "sub5":
				return encodeURIComponent(ctx.sub5 ?? "");
			case "timestamp":
				return encodeURIComponent(String(ctx.timestamp ?? 0));
			default:
				return "";
		}
	});
}

// ---------------------------------------------------------------------------
// Add-template draft + safeParse
// ---------------------------------------------------------------------------

export interface TemplateDraft {
	advertiser_id: string;
	label: string;
	url_template: string;
	method: PostbackMethod;
	events_csv: string;
	enabled: boolean;
}

export const EMPTY_TEMPLATE_DRAFT: TemplateDraft = {
	advertiser_id: "",
	label: "",
	url_template: "",
	method: "GET",
	events_csv: "*",
	enabled: true,
};

export interface ValidatedTemplateDraft {
	advertiser_id: string;
	label: string;
	url_template: string;
	method: PostbackMethod;
	events_csv: string;
	enabled: boolean;
}

export interface ParseOk<T> {
	success: true;
	data: T;
}
export interface ParseErr {
	success: false;
	errors: Record<string, string>;
}
export type ParseResult<T> = ParseOk<T> | ParseErr;

export const templateDraftSchema = {
	safeParse(input: TemplateDraft): ParseResult<ValidatedTemplateDraft> {
		const errors: Record<string, string> = {};

		const advertiser_id = (input.advertiser_id ?? "").trim();
		if (advertiser_id.length === 0) {
			errors.advertiser_id = "Advertiser ID is required.";
		} else if (advertiser_id.length > 120) {
			errors.advertiser_id = "Advertiser ID must be 120 chars or fewer.";
		}

		const label = (input.label ?? "").trim();
		if (label.length === 0) {
			errors.label = "Label is required.";
		} else if (label.length > 120) {
			errors.label = "Label must be 120 chars or fewer.";
		}

		const url_template = (input.url_template ?? "").trim();
		if (url_template.length === 0) {
			errors.url_template = "Postback URL is required.";
		} else if (!isHttpUrlTemplate(url_template)) {
			errors.url_template =
				"Must be a valid http(s) URL. {macro} tokens are allowed.";
		}

		const method: PostbackMethod = input.method === "POST" ? "POST" : "GET";

		const events_csv = (input.events_csv ?? "").trim() || "*";
		if (!isValidEventsCsv(events_csv)) {
			errors.events_csv =
				"Use '*' or a comma list of event names (a-z, 0-9, _, ., -).";
		}

		if (Object.keys(errors).length > 0) return { success: false, errors };
		return {
			success: true,
			data: {
				advertiser_id,
				label,
				url_template,
				method,
				events_csv,
				enabled: input.enabled !== false,
			},
		};
	},
};

// ---------------------------------------------------------------------------
// Presentation helpers
// ---------------------------------------------------------------------------

export function statusBadgeClass(status: PostbackStatus): string {
	switch (status) {
		case "fired":
			return "bg-emerald-50 text-emerald-700 border-emerald-200";
		case "duplicate":
			return "bg-sky-50 text-sky-700 border-sky-200";
		case "unknown_click":
			return "bg-amber-50 text-amber-700 border-amber-200";
		default:
			return "bg-red-50 text-red-700 border-red-200";
	}
}

export const STATUS_LABELS: Record<PostbackStatus, string> = {
	fired: "Fired",
	duplicate: "Duplicate",
	unknown_click: "Unknown click",
	rejected: "Rejected",
};

export function formatTimestamp(seconds: number | null): string {
	if (seconds === null || Number.isNaN(seconds)) return "—";
	const d = new Date(seconds * 1000);
	if (Number.isNaN(d.getTime())) return "—";
	return d.toISOString().replace("T", " ").replace(/\.\d{3}Z$/, "Z");
}

// ---------------------------------------------------------------------------
// Remote operations
// ---------------------------------------------------------------------------

const BASE = "/s2s-postback";

export async function listUrls(
	advertiserId?: string,
): Promise<PostbackUrlView[]> {
	const res = await apiClient.get<PostbackUrlView[]>(`${BASE}/urls`, {
		query: advertiserId ? { advertiser_id: advertiserId } : undefined,
	});
	return res;
}

export async function createUrl(
	payload: ValidatedTemplateDraft,
): Promise<PostbackUrlView> {
	return apiClient.post<PostbackUrlView>(`${BASE}/urls`, {
		advertiser_id: payload.advertiser_id,
		label: payload.label,
		url_template: payload.url_template,
		method: payload.method,
		events_csv: payload.events_csv,
		enabled: payload.enabled,
	});
}

export async function deleteUrl(id: string): Promise<void> {
	try {
		await apiClient.delete(`${BASE}/urls/${encodeURIComponent(id)}`);
	} catch (err) {
		if (err instanceof ApiError && err.status === 404) return;
		throw err;
	}
}

export interface SimulatePayload {
	click_id: string;
	event?: string;
	payout?: number | string;
	currency?: string;
	transaction_id?: string | null;
}

export async function simulate(payload: SimulatePayload): Promise<SimulateResponse> {
	return apiClient.post<SimulateResponse>(`${BASE}/simulate`, payload);
}

export async function listEvents(limit = 100): Promise<PostbackEventView[]> {
	return apiClient.get<PostbackEventView[]>(`${BASE}/events`, {
		query: { limit },
	});
}

// ---------------------------------------------------------------------------
// TanStack Query keys
// ---------------------------------------------------------------------------

export const s2sPostbackQueryKeys = {
	all: ["s2s-postback"] as const,
	urls: (advertiserId: string | undefined) =>
		[...s2sPostbackQueryKeys.all, "urls", advertiserId ?? "__all__"] as const,
	events: () => [...s2sPostbackQueryKeys.all, "events"] as const,
};
